import React, {useState, useCallback, useMemo, useRef, useEffect} from 'react';
import {isRTL} from '../../../../Localization';
import {
    Flex,
    Text,
    Button,
    Title,
    Icon,
    PriorityIcon,
    Scrollbar,
    ToolbarIcons
} from '@priority-software/priority-style-react';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {CircularLoader} from '../../../../components/CircularLoader/CircularLoader';
import {CSSTransition} from 'react-transition-group';
import styles from './styles.module.scss';
import chartAnalyticsImg from '../../../../images/foundation_graph-pie.svg';
import texts from './texts';
import {AnalysisType, fetchAnalysis, getDefaultAnalysisError} from '../../services/analysisService';
import {useChartContext} from '../../contexts/useChartContext';
import {serializeChartData} from '../../services/chartDataSerializer';
import {SingleSeriesChartTypesOptions, MultiSeriesChartTypesOptions} from '../../chartConsts';
import {
    getAnalysisOptionLabelByType,
    getDataSetValueByType,
    getAxesTitles,
    getDateFormat
} from './utils';
import {AnalysisOption} from './AnalysisOption';
import {getFormTitleAndEnameByBaseFormId} from '../../utils';
import {MarkdownRenderer} from '../../../../components/Markdown/MarkdownRenderer';
import {ChartAnalysisRs} from '../../types';
import {isModuleExists} from '../../../License/store';
import {advancedAnalyticsModuleId} from '../../consts';

type AnalysisPanelProps = {
    onHideContainer?: (hide: boolean) => void;
};

const TRANSITION_DURATION = 700;
// Removed unused constant LOADING_DURATION

const analysisOptions = [
    AnalysisType.EXECUTIVE,
    AnalysisType.COMPARATIVE,
    AnalysisType.STATISTICAL
];

const AnalysisPanel: React.FC<AnalysisPanelProps> = ({onHideContainer}) => {
    const [showAnalysis, setShowAnalysis] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showInitialContent, setShowInitialContent] = useState(true);
    const [analysisResponse, setAnalysisResponse] = useState<ChartAnalysisRs>(null);
    const [showCopiedMessage, setShowCopiedMessage] = useState(false);
    const [isAnalysisOptionsOpen, setIsAnalysisOptionsOpen] = useState(false);
    const [analysisOptionsData, setAnalysisOptionsData] = useState<DropdownItemData[]>([]);
    const [selectedAnalysisOption, setSelectedAnalysisOption] = useState<DropdownItemData>(null);

    // Get chart data from context
    const {chartConfig, chartData, aggregatedChartData, hasNoData} = useChartContext();

    const rtl = isRTL();

    // Ref to store the timeout ID for the copied message
    const copiedMessageTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Clear the timeout when component unmounts
    useEffect(() => {
        return () => {
            if (copiedMessageTimeoutRef.current) {
                clearTimeout(copiedMessageTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!chartConfig) return;
        const isMultiSeriesChart = Object.values<string>(MultiSeriesChartTypesOptions).includes(
            chartConfig.type
        );
        const analysisOptionsData: DropdownItemData[] = analysisOptions
            .map(option => {
                if (!isMultiSeriesChart && option === AnalysisType.COMPARATIVE) {
                    return null; // Skip comparative analysis for single-serie charts
                }
                return {
                    label: getAnalysisOptionLabelByType(option),
                    value: option
                };
            })
            .filter(Boolean);
        setAnalysisOptionsData(analysisOptionsData);
        const isGaugeOrKpiChart =
            chartConfig.type === SingleSeriesChartTypesOptions.kpi ||
            chartConfig.type === SingleSeriesChartTypesOptions.angulargauge;

        const hasLicense = isModuleExists(advancedAnalyticsModuleId);

        // Hide panel for KPI/gauge charts or when there's no data
        if (!hasLicense || isGaugeOrKpiChart || hasNoData) {
            onHideContainer?.(true);
        } else {
            onHideContainer?.(false);
        }
    }, [chartConfig, hasNoData, onHideContainer]);

    // Handle button click for any analysis type
    const handleAnalysisClick = useCallback(
        async (item: DropdownItemData) => {
            if (!chartConfig) return;
            const type = (item?.value as AnalysisType) || AnalysisType.EXECUTIVE;
            setSelectedAnalysisOption(item);
            setIsAnalysisOptionsOpen(false);
            setShowInitialContent(false);
            setIsLoading(true);

            try {
                // Serialize the chart data to the format required by the API
                let serializedData; //: ChartAnalysisRq | AIChartData;
                const baseFormNodeId = chartConfig.baseForm;
                const {baseFormTitle, ename} = getFormTitleAndEnameByBaseFormId(baseFormNodeId);

                // Use aggregated data if available, otherwise fall back to raw data
                if (aggregatedChartData) {
                    const dataSet = getDataSetValueByType(chartConfig.type, aggregatedChartData);
                    const dateFormat = getDateFormat(chartConfig);
                    const axesTitles = getAxesTitles(chartConfig);
                    // Create serialized data from aggregated chart data
                    serializedData = {
                        language: rtl ? 'he' : 'en', // Use Hebrew for RTL mode
                        analysisType: type,
                        ename,
                        baseFormTitle,
                        chartType: chartConfig.type,
                        caption: chartConfig.chartName,
                        subCaption: chartConfig.subTitle || '',
                        dataSet,
                        ...dateFormat,
                        ...axesTitles
                    };
                } else if (chartConfig && chartData) {
                    // Fall back to raw data if aggregated data is not available
                    console.log('Falling back to raw chart data for analysis');
                    serializedData = serializeChartData(chartConfig, chartData, type);
                }

                const analysisRes = await fetchAnalysis(serializedData);
                setAnalysisResponse(analysisRes);
                setIsLoading(false);
                setShowAnalysis(true);
            } catch (error) {
                console.error('Error fetching analysis:', error);
                const defaulterror = getDefaultAnalysisError();
                setAnalysisResponse(defaulterror);
                setIsLoading(false);
                setShowAnalysis(true);
            }
        },
        [chartConfig, chartData, aggregatedChartData]
    );

    // Handle back button click
    const handleBackClick = useCallback(() => {
        setShowAnalysis(false);
        // Reset content after transition completes
        setTimeout(() => {
            setShowInitialContent(true);
        }, TRANSITION_DURATION);
    }, []);

    const analysisContent = useMemo(() => {
        if (!analysisResponse) return null;
        return analysisResponse;
    }, [analysisResponse]);

    // Analysis title based on response
    const analysisTitle = useMemo(() => {
        if (analysisResponse?.analysisContent) {
            switch (selectedAnalysisOption?.value) {
                case AnalysisType.EXECUTIVE:
                    return texts.executiveAnalysis;
                case AnalysisType.COMPARATIVE:
                    return texts.comparativeAnalysis;
                case AnalysisType.STATISTICAL:
                    return texts.statisticalAnalysis;
                default:
                    return '';
            }
        }
        return texts.analysisError;
    }, [selectedAnalysisOption, analysisResponse]);

    // Get the full analysis text for copying
    const getAnalysisTextForCopy = useCallback(() => {
        if (!analysisResponse) return '';

        let text = analysisTitle ? `${analysisTitle}\n\n` : '';

        if (analysisResponse?.analysisContent && analysisResponse?.analysisContent?.length > 0) {
            text += analysisResponse.analysisContent;
        }
        console.log('Copy text:', text);

        return text;
    }, [analysisResponse, analysisTitle]);

    // Handle copy text button click
    const handleCopyText = useCallback(() => {
        const textToCopy = getAnalysisTextForCopy();

        // Copy text to clipboard
        navigator.clipboard.writeText(textToCopy).then(() => {
            // Show the copied message
            setShowCopiedMessage(true);

            // Clear any existing timeout
            if (copiedMessageTimeoutRef.current) {
                clearTimeout(copiedMessageTimeoutRef.current);
            }

            // Hide the message after 2 seconds
            copiedMessageTimeoutRef.current = setTimeout(() => {
                setShowCopiedMessage(false);
            }, 2000);
        });
    }, [getAnalysisTextForCopy]);

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                {showInitialContent && (
                    <Flex direction="column" componentClasses={styles.initialView}>
                        {/* Reorganized layout for better vertical centering */}
                        <div className={styles.imageContainer}>
                            <img
                                src={chartAnalyticsImg}
                                alt={texts.aiAnalysis}
                                className={styles.analysisImage}
                            />
                        </div>
                        <Title
                            skin="h3"
                            textColor="primary-500"
                            componentClasses={styles.title}
                            dataTestId="ai-analysis-title"
                        >
                            {texts.aiAnalysis}
                        </Title>
                        {/* Loading state or content */}
                        {isLoading ? (
                            <Flex
                                direction="column"
                                alignItems="center"
                                justifyContent="center"
                                componentClasses={styles.inlineLoaderContainer}
                            >
                                <CircularLoader height={48} />
                                <Text componentClasses={styles.loaderText}>
                                    {texts.analyzingData}
                                </Text>
                            </Flex>
                        ) : (
                            <>
                                <Text componentClasses={styles.subtitle}>
                                    {texts.selectDisplayChartAnalysis}
                                </Text>
                                <Flex
                                    direction="column"
                                    alignItems="center"
                                    componentClasses={styles.analysisOptionsDropdown}
                                >
                                    <Button
                                        skin="aierp"
                                        size="medium"
                                        wideButton
                                        suffixIcon={
                                            <Icon
                                                icon={
                                                    isAnalysisOptionsOpen
                                                        ? PriorityIcon.arrowUpThin
                                                        : PriorityIcon.arrowBottom
                                                }
                                            />
                                        }
                                        componentClasses={styles.aierpButton}
                                        onClick={() =>
                                            setIsAnalysisOptionsOpen(!isAnalysisOptionsOpen)
                                        }
                                        children={
                                            <Text skin="paragraph-2">
                                                {texts.selectAnalysisType}
                                            </Text>
                                        }
                                    />

                                    {isAnalysisOptionsOpen && (
                                        <Flex
                                            direction="column"
                                            alignItems="center"
                                            componentClasses={styles.analysisOptionsContainer}
                                            dataTestId="analysis-options-dropdown"
                                        >
                                            {analysisOptionsData.map(item => {
                                                return (
                                                    <AnalysisOption
                                                        key={item.label}
                                                        item={item}
                                                        onSelect={handleAnalysisClick}
                                                    />
                                                );
                                            })}
                                        </Flex>
                                    )}
                                </Flex>
                            </>
                        )}
                    </Flex>
                )}

                {/* Loading state when initial content is hidden */}
                {isLoading && !showInitialContent && (
                    <Flex
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        componentClasses={styles.loaderContainer}
                    >
                        <div>
                            <CircularLoader height={48} />
                        </div>
                        <Text componentClasses={styles.loaderText}>{texts.analyzingData}</Text>
                    </Flex>
                )}

                {/* Analysis view */}
                <CSSTransition
                    in={showAnalysis}
                    timeout={TRANSITION_DURATION}
                    classNames={{
                        enter: styles.analysisViewEnter,
                        enterActive: styles.analysisViewEnterActive,
                        exit: styles.analysisViewExit,
                        exitActive: styles.analysisViewExitActive
                    }}
                    unmountOnExit
                >
                    <Flex
                        direction="column"
                        componentClasses={styles.analysisView}
                        justifyContent={!!analysisContent?.error ? 'flex-start' : 'space-between'}
                    >
                        <Flex direction="column" componentClasses={styles.header}>
                            <Button
                                onClick={handleBackClick}
                                skin="text"
                                prefixIcon={
                                    <Icon
                                        icon={
                                            rtl ? PriorityIcon.arrowRight : PriorityIcon.arrowLeft
                                        }
                                    />
                                }
                                componentClasses={styles.backButton}
                            >
                                {texts.back}
                            </Button>
                            <Title
                                skin="h3"
                                textColor="primary-700"
                                componentClasses={styles.analysisTitle}
                                dataTestId="analysis-title"
                            >
                                {analysisTitle}
                            </Title>
                        </Flex>
                        {!!analysisContent?.error ? (
                            <Text skin="paragraph-1">
                                {analysisContent.error || texts.errorMessage}
                            </Text>
                        ) : (
                            <div className={styles.analysisContent}>
                                <Scrollbar
                                    rtl={rtl}
                                    componentClasses={styles.analysisContentScrollArea}
                                >
                                    <MarkdownRenderer
                                        content={analysisContent?.analysisContent || ''}
                                        componentClasses={styles.markdownRenderer}
                                    />
                                    <div className={styles.copyButtonContainer}>
                                        <Button
                                            onClick={handleCopyText}
                                            skin={showCopiedMessage ? 'primary' : 'text'}
                                            prefixIcon={
                                                <Icon
                                                    icon={
                                                        showCopiedMessage
                                                            ? PriorityIcon.check
                                                            : ToolbarIcons.toolbarCopy
                                                    }
                                                />
                                            }
                                            componentClasses={`${styles.copyButton} ${
                                                showCopiedMessage ? styles.copyButtonActive : ''
                                            }`}
                                            aria-label={
                                                showCopiedMessage
                                                    ? texts.copiedToClipboard
                                                    : texts.copyText
                                            }
                                        >
                                            {showCopiedMessage
                                                ? texts.copiedToClipboard
                                                : texts.copyText}
                                        </Button>
                                    </div>
                                </Scrollbar>
                            </div>
                        )}
                    </Flex>
                </CSSTransition>
            </div>
        </div>
    );
};

export default AnalysisPanel;
